import { useEffect, useMemo, useState } from "react";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../../lib/api";
import {
  useResource, PageTitle, Panel, Loading, ErrorState, EmptyState, Pill, DemoBadge, fmtDateTime,
} from "../../components/app/Kit";

const TABS = ["all", "sms", "whatsapp", "email", "calls"];

export default function Messages() {
  const { data, state, error, reload } = useResource("/conversations");
  const [tab, setTab] = useState("all");
  const [selected, setSelected] = useState(null);
  const [thread, setThread] = useState(null);
  const [note, setNote] = useState("");
  const [threadState, setThreadState] = useState("idle");

  const rows = useMemo(() => {
    const list = data || [];
    if (tab === "all") return list;
    if (tab === "calls") return list.filter((c) => c.channel === "call");
    return list.filter((c) => c.channel === tab);
  }, [data, tab]);

  useEffect(() => {
    if (!selected) return;
    setThreadState("loading");
    api
      .get(`/conversation-detail/${selected}`)
      .then(({ data: d }) => {
        setThread(d);
        setThreadState("ready");
      })
      .catch(() => setThreadState("error"));
  }, [selected]);

  const addNote = async () => {
    if (note.trim().length < 2) return;
    try {
      await api.post(`/conversation-detail/${selected}/note`, { body: note.trim() });
      const { data: d } = await api.get(`/conversation-detail/${selected}`);
      setThread(d);
      setNote("");
      toast.success("Internal note added.");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div data-testid="messages-page">
      <PageTitle
        testId="messages-title"
        title="Unified inbox"
        sub="SMS, WhatsApp, email and call threads in one place, with AI and human attribution."
        action={<DemoBadge />}
      />

      <div
        data-testid="messaging-integration-notice"
        className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-300/25 bg-amber-300/[0.05] p-4"
      >
        <p className="text-xs text-amber-200">
          <span className="font-medium">Messaging providers not connected.</span> Outbound sending is disabled —
          you can add internal notes, but no external message will be delivered.
        </p>
        <a
          href="/app/integrations"
          data-testid="messages-connect-integration"
          className="rounded-md border border-amber-300/30 px-3 py-1.5 text-[11px] text-amber-200 hover:bg-amber-300/10"
        >
          Connect integration
        </a>
      </div>

      <div className="mb-4 flex flex-wrap gap-1.5">
        {TABS.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            data-testid={`messages-tab-${t}`}
            className={`rounded-md border px-3 py-1.5 text-[11px] uppercase tracking-[0.12em] ${
              tab === t ? "border-elv-green/35 bg-elv-green/[0.08] text-elv-green" : "border-elv-line bg-white/[0.02] text-elv-dim"
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      <div className="grid gap-4 lg:grid-cols-[0.9fr_1.1fr]">
        <div>
          {state === "loading" ? <Loading rows={7} testId="messages-loading" /> : null}
          {state === "error" ? <ErrorState message={error} onRetry={reload} testId="messages-error" /> : null}
          {state === "ready" && rows.length === 0 ? (
            <EmptyState
              icon="MessagesSquare"
              title="No conversations"
              sub="Your AI receptionist activity will appear here."
              testId="messages-empty"
            />
          ) : null}
          {state === "ready" && rows.length > 0 ? (
            <div className="grid gap-2">
              {rows.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setSelected(c.id)}
                  data-testid={`conversation-${c.id}`}
                  className={`glass glass-hover rounded-lg p-4 text-left ${selected === c.id ? "border-elv-green/35" : ""}`}
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="flex items-center gap-2 text-xs font-medium text-elv-text">
                      {c.unread ? <span className="h-1.5 w-1.5 rounded-full bg-elv-green" /> : null}
                      {c.customer_name}
                    </span>
                    <div className="flex items-center gap-2">
                      <Pill tone="cyan">{c.channel}</Pill>
                      <Pill tone={c.handled_by === "ai" ? "green" : "dim"}>{c.handled_by}</Pill>
                    </div>
                  </div>
                  <p className="mt-2 truncate text-[11px] text-elv-dim">{c.last_message}</p>
                  <p className="mt-2 font-mono text-[10px] text-elv-dim">{fmtDateTime(c.updated_at)}</p>
                </button>
              ))}
            </div>
          ) : null}
        </div>

        <div className="lg:sticky lg:top-20 lg:self-start">
          {!selected ? (
            <EmptyState
              icon="Inbox"
              title="Select a conversation"
              sub="Open a thread to see messages, assignment and the next action."
              testId="thread-empty"
            />
          ) : threadState === "loading" ? (
            <Loading rows={5} testId="thread-loading" />
          ) : threadState === "error" ? (
            <ErrorState message="We couldn't load this conversation." onRetry={() => setSelected(selected)} testId="thread-error" />
          ) : (
            <Panel
              testId="thread-panel"
              title={`${thread.conversation.customer_name} · ${thread.conversation.channel}`}
              footer={`Assigned to ${thread.conversation.assigned_to} · Next action: ${thread.conversation.next_action}`}
            >
              <div className="scrollbar-thin grid max-h-[420px] gap-2 overflow-y-auto">
                {thread.messages.map((m) => (
                  <div
                    key={m.id}
                    data-testid={`message-${m.id}`}
                    className={`max-w-[85%] rounded-lg border px-3 py-2.5 text-[11px] ${
                      m.direction === "inbound"
                        ? "border-white/[0.07] bg-white/[0.025] text-elv-dim"
                        : m.direction === "internal"
                        ? "mx-auto border-[#6B4C9A]/30 bg-[#6B4C9A]/[0.1] text-[#d3bcf5]"
                        : "ml-auto border-elv-green/25 bg-elv-green/[0.07] text-elv-text"
                    }`}
                  >
                    <span className="mb-1 block text-[9px] uppercase tracking-[0.18em] text-elv-dim">
                      {m.sender} · {m.handled_by} · {fmtDateTime(m.created_at)}
                    </span>
                    {m.body}
                  </div>
                ))}
              </div>

              <div className="mt-4 border-t border-elv-line pt-4">
                <label className="text-[10px] uppercase tracking-[0.16em] text-elv-dim">Internal note</label>
                <textarea
                  rows={2}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Visible to your team only — no external message is sent."
                  data-testid="thread-note-input"
                  className="mt-2 w-full rounded-md border border-elv-line bg-white/[0.02] p-3 text-xs text-elv-text outline-none focus:border-elv-green/40"
                />
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={addNote}
                    data-testid="thread-note-save"
                    className="rounded-md bg-elv-green px-4 py-2 text-xs font-semibold text-[#04120A]"
                  >
                    Add note
                  </button>
                  <button
                    type="button"
                    disabled
                    title="Connect a messaging provider to enable sending"
                    data-testid="thread-send-disabled"
                    className="inline-flex cursor-not-allowed items-center gap-2 rounded-md border border-elv-line px-4 py-2 text-xs text-elv-dim opacity-60"
                  >
                    <Icons.SendHorizonal className="h-3.5 w-3.5" /> Send message — connection required
                  </button>
                </div>
              </div>
            </Panel>
          )}
        </div>
      </div>
    </div>
  );
}
